import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, FadeUp, ChapterLabel } from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";
import { chantiers as chantiersFallback } from "@/data/chantiers";
import { useSiteData, resolveSrc, defaultSettings } from "@/api";

const Realisations = () => {
  const site = useSiteData({ chantiers: chantiersFallback, services: null, settings: defaultSettings });
  const listeChantiers = site.chantiers || chantiersFallback;

  return (
    <>
      <Seo
        title="Réalisations avant/après — Peintre à Angoulême & Cognac | Alexis Peinture"
        description="Galerie avant/après des chantiers de peinture intérieure décorative réalisés par Alexis Peinture dans le Grand Angoulême et le Grand Cognac."
      />

      <section className="px-6 pb-16 pt-40 lg:px-12 lg:pt-48">
        <div className="mx-auto max-w-7xl">
          <ChapterLabel number="01" label="Réalisations" />
          <h1
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            data-testid="realisations-title"
          >
            <MaskedLines
              lines={[
                "La preuve",
                <span key="l2">
                  par <em className="italic text-clay">l'image.</em>
                </span>,
              ]}
            />
          </h1>
          <FadeUp delay={0.2} className="mt-8 max-w-xl">
            <p className="leading-relaxed text-bark">
              Faites glisser le curseur sur chaque photo pour comparer l'avant et
              l'après. C'est là que le soin du détail prend tout son sens.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-12" data-testid="realisations-gallery">
        <div className="grid gap-x-10 gap-y-16 md:grid-cols-2">
          {listeChantiers.map((c, i) => (
            <FadeUp key={c.id} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:translate-y-16" : ""}>
              <article data-testid={`chantier-${c.id}`}>
                {c.photo ? (
                  <div className="aspect-[4/3] w-full overflow-hidden bg-plaster">
                    <img
                      src={resolveSrc(c.photo)}
                      alt={`${c.titre}${c.ville ? ` à ${c.ville}` : ""}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out hover:scale-[1.04]"
                    />
                  </div>
                ) : (
                  <BeforeAfter
                    image={c.apres || c.image}
                    before={c.avant}
                    alt={`${c.titre}${c.ville ? ` à ${c.ville}` : ""}`}
                    testid={`before-after-${c.id}`}
                  />
                )}
                <div className="mt-6 flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-serif text-2xl tracking-tight">{c.titre}</h2>
                  {c.ville && (
                    <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-taupe">
                      <MapPin size={12} className="text-clay" /> {c.ville}
                    </p>
                  )}
                </div>
                <p className="mt-1 text-sm text-bark">{c.prestation}</p>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-ink py-24 text-cream lg:py-32">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-12">
          <FadeUp>
            <h2 className="mx-auto max-w-2xl font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
              Votre intérieur mérite <em className="italic text-sand">le même soin.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/contact"
              data-testid="realisations-cta-devis"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-10 py-5 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-sand hover:text-ink"
            >
              Demander mon devis gratuit
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Realisations;
